import React, { useMemo } from 'react';

import classes from '@exp/utilities/buildClassName';

import './Stepper.less';

const BAR_HEIGHT = '10px';

type StepperProps = {
  active?: boolean;
  label?: string | React.ReactNode;
  prefill?: boolean;
  segments?: number;
  segmentsComplete?: number;
  smooth?: boolean;
};

const Stepper = ({
  active = false,
  label = '',
  prefill = false,
  segments = 1,
  segmentsComplete = 0,
  smooth = false,
}: StepperProps) => {
  const complete = segments === segmentsComplete;
  const stepSize = 100 / segments;

  const steps = useMemo(
    () =>
      Array.from({ length: segments + 1 }).map((_, i) => {
        if (i === 0 && prefill) {
          return {
            class: classes('prefill', segmentsComplete === 0 && 'last'),
            size: BAR_HEIGHT,
            key: i.toString(),
          };
        }
        if (i > 0 && i <= segmentsComplete) {
          return {
            class: classes('completed', i === segmentsComplete && 'last'),
            size: `${stepSize}%`,
            key: i.toString(),
          };
        }
        return {
          class: 'empty',
          size: `${stepSize}%`,
          key: i.toString(),
        };
      }),
    [prefill, segments, segmentsComplete, stepSize],
  );
  const renderSteps = useMemo(() => {
    if (!smooth) {
      return steps.map((step) => (
        <div className={classes('step', step.class)} style={{ '--step-size': `${step.size}` }} key={step.key} />
      ));
    }
    const combinedSize = (100 / segments) * segmentsComplete;
    return <div className="step completed last" style={{ '--step-size': `${combinedSize}%` }} />;
  }, [steps, segmentsComplete, segments, smooth]);

  return (
    <div className={classes('stepper', active && 'active')}>
      {!!label && <h3 className="stepper-title">{label}</h3>}
      <div className={classes('stepper-row', complete && 'complete')}>
        <div className="stepper-content" style={{ '--bar-height': BAR_HEIGHT }}>
          {renderSteps}
        </div>
        <i className="exp-ux-check exp-ux-medium" />
      </div>
    </div>
  );
};

Stepper.displayName = 'Stepper';

export default Stepper;
